/**
 * IndexedDB utility functions
 * Provides a simple interface for working with IndexedDB
 */

const DB_VERSION = 4;
const DB_NAME = `whitefoxboutique_v${DB_VERSION}`;
const STORES = {
  PRODUCTS_CACHE: 'products',
  WISHLIST: 'wishlist',
  PRODUCTS_NOLAYOUT: 'products_nolayout',
};

/**
 * Cache for open database connections to avoid reopening
 * @type {Object}
 * @private
 */
let dbCache = null;
let dbPromise = null;

/**
 * Opens a connection to the IndexedDB database
 * @returns {Promise<IDBDatabase>} Database connection
 */
const openDB = () => {
  // Return cached connection if available
  if (dbCache) {
    return Promise.resolve(dbCache);
  }

  // Return existing promise if a connection is in progress
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.error);
      dbPromise = null;
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      dbCache = event.target.result;

      // Clear cache when connection is closed
      dbCache.onclose = () => {
        dbCache = null;
      };

      // Clear cache on version change
      dbCache.onversionchange = () => {
        dbCache.close();
        dbCache = null;
      };

      resolve(dbCache);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object stores if they don't exist
      if (!db.objectStoreNames.contains(STORES.PRODUCTS_CACHE)) {
        db.createObjectStore(STORES.PRODUCTS_CACHE, { keyPath: 'id' });
      }

      if (!db.objectStoreNames.contains(STORES.WISHLIST)) {
        db.createObjectStore(STORES.WISHLIST, { keyPath: 'id' });
      }

      if (!db.objectStoreNames.contains(STORES.PRODUCTS_NOLAYOUT)) {
        db.createObjectStore(STORES.PRODUCTS_NOLAYOUT, { keyPath: 'sectionKey' });
      }
    };
  });

  return dbPromise;
};

/**
 * Performs a transaction on the database
 * @param {string} storeName - Name of the object store
 * @param {string} mode - Transaction mode ('readonly' or 'readwrite')
 * @param {Function} callback - Function to execute with the transaction
 * @returns {Promise<any>} Result of the transaction
 */
const withStore = async (storeName, mode, callback) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);

    transaction.onerror = (event) => {
      console.error(`Transaction error on ${storeName}:`, event.target.error);
      reject(event.target.error);
    };

    try {
      const result = callback(store);
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Gets an item from an object store
 * @param {string} storeName - Name of the object store
 * @param {string|number} key - Key of the item to get
 * @returns {Promise<any>} The item, or undefined if not found
 */
const getItem = (storeName, key) => {
  return withStore(storeName, 'readonly', (store) => {
    return new Promise((resolve, reject) => {
      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  });
};

/**
 * Puts an item into an object store
 * @param {string} storeName - Name of the object store
 * @param {Object} item - Item to store
 * @returns {Promise<any>} Result of the operation
 */
const putItem = (storeName, item) => {
  return withStore(storeName, 'readwrite', (store) => {
    return new Promise((resolve, reject) => {
      const request = store.put(item);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  });
};

/**
 * Deletes an item from an object store
 * @param {string} storeName - Name of the object store
 * @param {string|number} key - Key of the item to delete
 * @returns {Promise<void>}
 */
const deleteItem = (storeName, key) => {
  return withStore(storeName, 'readwrite', (store) => {
    return new Promise((resolve, reject) => {
      const request = store.delete(key);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  });
};

/**
 * Gets all items from an object store
 * @param {string} storeName - Name of the object store
 * @returns {Promise<Array>} All items in the store
 */
const getAllItems = (storeName) => {
  return withStore(storeName, 'readonly', (store) => {
    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  });
};

/**
 * Clears all items from an object store
 * @param {string} storeName - Name of the object store
 * @returns {Promise<void>}
 */
const clearStore = (storeName) => {
  return withStore(storeName, 'readwrite', (store) => {
    return new Promise((resolve, reject) => {
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  });
};

window.WFDB = {
  STORES,
  openDB,
  getItem,
  putItem,
  deleteItem,
  getAllItems,
  clearStore,
};

export { STORES, openDB, getItem, putItem, deleteItem, getAllItems, clearStore };
